import type { AnalysisReport, AnalysisResult, ContainerMetadata } from "../types";
import { element } from "../utils/dom";
import { escapeHtml, formatBytes, formatKhz, formatTime } from "../utils/format";

type PaletteName = keyof typeof PALETTES;
type ColorStop = readonly [number, readonly [number, number, number]];

const PALETTES = {
  ember: [
    [0, [4, 5, 10]], [0.18, [28, 12, 62]], [0.42, [112, 26, 91]],
    [0.66, [225, 65, 75]], [0.84, [255, 169, 42]], [1, [255, 247, 195]],
  ],
  aurora: [
    [0, [3, 8, 14]], [0.2, [10, 33, 65]], [0.43, [17, 104, 116]],
    [0.67, [49, 211, 157]], [0.85, [196, 250, 73]], [1, [248, 255, 220]],
  ],
  ice: [
    [0, [3, 5, 12]], [0.23, [14, 33, 78]], [0.5, [36, 101, 170]],
    [0.75, [75, 211, 235]], [1, [231, 255, 255]],
  ],
  mono: [
    [0, [2, 3, 5]], [0.35, [44, 48, 55]], [0.7, [147, 153, 163]],
    [1, [255, 255, 255]],
  ],
} as const satisfies Record<string, readonly ColorStop[]>;

export interface VisualizationState {
  analysis: AnalysisResult;
  mix: Float32Array;
  audioBuffer: AudioBuffer;
  file: File;
  metadata: ContainerMetadata;
  palette: PaletteName;
  floorDb: number;
}

export function renderHero(): void {
  const canvas = element<HTMLCanvasElement>("#heroCanvas");
  const context = canvas.getContext("2d");
  if (!context) return;
  context.fillStyle = "#06080d";
  context.fillRect(0, 0, canvas.width, canvas.height);

  for (let x = 0; x < canvas.width; x += 2) {
    const time = x / canvas.width;
    const cutoff = 0.18 + 0.05 * Math.sin(time * 8);
    for (let y = 0; y < canvas.height; y += 2) {
      const frequency = 1 - y / canvas.height;
      const bands =
        Math.exp(-((frequency - (0.15 + 0.03 * Math.sin(time * 22))) ** 2) / 0.002) +
        0.8 * Math.exp(-((frequency - (0.34 + 0.08 * Math.sin(time * 10))) ** 2) / 0.006) +
        0.4 * Math.exp(-((frequency - (0.56 + 0.05 * Math.sin(time * 31))) ** 2) / 0.009);
      const value = Math.max(
        0,
        Math.min(1, (bands + Math.random() * 0.18 * (1 - frequency)) * (frequency < 0.82 ? 1 : cutoff)),
      );
      const [red, green, blue] = colorAt(PALETTES.ember, value);
      context.fillStyle = `rgb(${red},${green},${blue})`;
      context.fillRect(x, y, 2, 2);
    }
  }
}

export function populateFileHeader(file: File, audioBuffer: AudioBuffer): void {
  const extension = file.name.split(".").pop()?.toUpperCase() || "AUDIO";
  element("#fileBadge").textContent = extension.slice(0, 5);
  element("#fileName").textContent = file.name;
  element("#durationFact").textContent = formatTime(audioBuffer.duration);
  element("#sampleRateFact").textContent =
    `${(audioBuffer.sampleRate / 1000).toFixed(audioBuffer.sampleRate % 1000 ? 1 : 0)} kHz`;
  element("#channelsFact").textContent =
    audioBuffer.numberOfChannels === 1 ? "Mono" : `${audioBuffer.numberOfChannels} canais`;
  element("#sizeFact").textContent = formatBytes(file.size);
}

export function renderVisualization(state: VisualizationState): void {
  renderSpectrogram(state);
  renderWaveform(state.mix);
  renderAxes(state.audioBuffer);
  updateGradient(state.palette);
}

export function renderSpectrogram(state: VisualizationState): void {
  const canvas = element<HTMLCanvasElement>("#spectrogramCanvas");
  const rect = canvas.parentElement?.getBoundingClientRect();
  if (!rect) return;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  canvas.width = Math.max(1, Math.floor(rect.width * dpr));
  canvas.height = Math.max(1, Math.floor(rect.height * dpr));
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) return;
  const image = context.createImageData(canvas.width, canvas.height);
  const { frameCount, binCount, spectrogram } = state.analysis;
  const palette = PALETTES[state.palette];
  const floorNormalized = ((state.floorDb + 120) / 120) * 255;

  for (let y = 0; y < canvas.height; y++) {
    const bin = Math.min(binCount - 1, Math.floor((1 - y / canvas.height) * binCount));
    for (let x = 0; x < canvas.width; x++) {
      const frame = Math.min(frameCount - 1, Math.floor((x / canvas.width) * frameCount));
      const raw = spectrogram[frame * binCount + bin];
      const normalized = Math.max(0, Math.min(1, (raw - floorNormalized) / (255 - floorNormalized)));
      const [red, green, blue] = colorAt(palette, normalized ** 0.82);
      const offset = (y * canvas.width + x) * 4;
      image.data.set([red, green, blue, 255], offset);
    }
  }
  context.putImageData(image, 0, 0);
  drawGrid(context, canvas.width, canvas.height, dpr, state);
}

export function renderWaveform(mix: Float32Array): void {
  const canvas = element<HTMLCanvasElement>("#waveformCanvas");
  const rect = canvas.parentElement?.getBoundingClientRect();
  if (!rect) return;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  canvas.width = Math.max(1, Math.floor(rect.width * dpr));
  canvas.height = Math.max(1, Math.floor(rect.height * dpr));
  const context = canvas.getContext("2d");
  if (!context) return;
  context.fillStyle = "#090c12";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.strokeStyle = "rgba(200,255,61,.62)";
  context.lineWidth = Math.max(1, dpr);
  context.beginPath();
  const step = Math.max(1, Math.floor(mix.length / canvas.width));

  for (let x = 0; x < canvas.width; x++) {
    let minimum = 1;
    let maximum = -1;
    const start = x * step;
    for (let index = start; index < Math.min(mix.length, start + step); index++) {
      minimum = Math.min(minimum, mix[index]);
      maximum = Math.max(maximum, mix[index]);
    }
    context.moveTo(x, ((1 - maximum) * canvas.height) / 2);
    context.lineTo(x, ((1 - minimum) * canvas.height) / 2);
  }
  context.stroke();
}

export function renderReport(report: AnalysisReport, metadata: ContainerMetadata): void {
  const { verdict, findings, bitrate, cutoff, stats } = report;
  const colors = { danger: "var(--red)", warning: "var(--amber)", good: "var(--acid)" };
  const color = colors[verdict.level];
  const card = element<HTMLElement>("#verdictCard");
  card.style.borderTopColor = color;
  const icon = element<HTMLElement>("#verdictIcon");
  icon.style.borderColor = color;
  icon.style.color = color;
  icon.textContent = verdict.icon;
  element("#verdictTitle").textContent = verdict.title;
  element("#verdictSubtitle").textContent = verdict.subtitle;
  element("#confidenceValue").textContent = `${verdict.confidence}%`;
  const confidenceBar = element<HTMLElement>("#confidenceBar");
  confidenceBar.style.width = `${verdict.confidence}%`;
  confidenceBar.style.background = color;
  element("#verdictNote").textContent = verdict.note;

  element("#cutoffMetric").textContent = formatKhz(cutoff.hz);
  element("#cutoffDetail").textContent = cutoff.sharp
    ? `Queda de ${cutoff.dropDb.toFixed(1)} dB`
    : "Transição gradual";
  element("#dynamicMetric").textContent = `${stats.crestDb.toFixed(1)} dB`;
  element("#dynamicDetail").textContent = `LRA aprox. ${stats.loudnessRange.toFixed(1)} dB`;
  element("#clippingMetric").textContent =
    stats.clippingPercent < 0.0001 ? "0" : `${stats.clippingPercent.toFixed(3)}%`;
  element("#clippingDetail").textContent = `${stats.clipped.toLocaleString("pt-BR")} amostras`;
  element("#bitrateMetric").textContent = `${Math.round(bitrate)} kbps`;
  element("#bitrateDetail").textContent = metadata.bitDepth
    ? `${metadata.bitDepth}-bit declarado`
    : "Estimado pelo tamanho";
  element("#findings").innerHTML = findings
    .map(
      ({ severity, title, text }) => `
        <article class="finding ${severity}">
          <strong>${escapeHtml(title)}</strong>
          <p>${escapeHtml(text)}</p>
        </article>`,
    )
    .join("");
}

export function showCursorReadout(event: MouseEvent, audioBuffer: AudioBuffer): void {
  const canvas = event.currentTarget as HTMLCanvasElement;
  const rect = canvas.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;
  const readout = element<HTMLElement>("#cursorReadout");
  readout.textContent =
    `${formatTime((x / rect.width) * audioBuffer.duration)} · ${formatKhz((1 - y / rect.height) * audioBuffer.sampleRate / 2)}`;
  readout.style.left = `${Math.min(rect.width - 115, x + 12)}px`;
  readout.style.top = `${Math.max(4, y - 28)}px`;
  readout.classList.remove("hidden");
}

export function updateGradient(paletteName: PaletteName): void {
  const stops = PALETTES[paletteName].map(
    ([position, color]) => `rgb(${color.join(",")}) ${position * 100}%`,
  );
  element<HTMLElement>("#gradientBar").style.background =
    `linear-gradient(90deg, ${stops.join(",")})`;
}

function renderAxes(audioBuffer: AudioBuffer): void {
  const nyquist = audioBuffer.sampleRate / 2;
  element("#frequencyAxis").innerHTML = Array.from({ length: 6 }, (_, index) => {
    const hz = nyquist * (1 - index / 5);
    return `<span style="top:${(index / 5) * 100}%">${formatKhz(hz)}</span>`;
  }).join("");
  element("#timeAxis").innerHTML = Array.from(
    { length: 6 },
    (_, index) => `<span>${formatTime((audioBuffer.duration * index) / 5)}</span>`,
  ).join("");
}

function drawGrid(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  dpr: number,
  state: VisualizationState,
): void {
  context.save();
  context.strokeStyle = "rgba(255,255,255,.06)";
  context.lineWidth = dpr;
  for (let index = 1; index < 10; index++) {
    const x = Math.round((width * index) / 10);
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x, height);
    context.stroke();
  }
  for (let index = 1; index < 5; index++) {
    const y = Math.round((height * index) / 5);
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(width, y);
    context.stroke();
  }
  if (state.analysis.cutoff.sharp) {
    const y = height * (1 - state.analysis.cutoff.hz / (state.audioBuffer.sampleRate / 2));
    context.setLineDash([6 * dpr, 5 * dpr]);
    context.strokeStyle = "rgba(255,255,255,.5)";
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(width, y);
    context.stroke();
  }
  context.restore();
}

function colorAt(
  palette: readonly ColorStop[],
  value: number,
): [number, number, number] {
  for (let index = 1; index < palette.length; index++) {
    if (value <= palette[index][0]) {
      const [previousPosition, previousColor] = palette[index - 1];
      const [position, color] = palette[index];
      const ratio = (value - previousPosition) / (position - previousPosition);
      return previousColor.map((channel, channelIndex) =>
        Math.round(channel + (color[channelIndex] - channel) * ratio),
      ) as [number, number, number];
    }
  }
  return [...palette.at(-1)![1]];
}

export function isPaletteName(value: string): value is PaletteName {
  return value in PALETTES;
}
